import React, { useCallback, useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import { StartTimeEventEmit } from "react-schedule-meeting";
import SubjectsSelection, { Subject } from "./SubjectsSelection";
import LessonDurationSelection from "./LessonDurationSelection";
import TeacherTimeSelection from "./TeacherTimeSelection";
import LoginRegisterFormLessons from "./LoginRegisterFormLessons";
import ReservationTimer from "./ReservationTimer";
import api from "common/api";
import { ISubject, SubjectKind } from "common/types";
import "./LessonsReservationPage.css";
import OrderPaymentsLesson from "../Payments/OrderPaymentsLesson";
import usePageTitle from "../../../common/usePageTitle";
import { toast } from "react-toastify";
import { LESSON_RESERVATION_MIN_HOURS_AHEAD } from "common/constants";

interface ReservationData {
  reservationToken: string;
  reservedUntil: Date;
  calendarId: number;
}

const RESERVATION_STORAGE_KEY = "lessonReservation";

function LessonsReservationPage() {
  const getMinimumAllowedLessonStartTime = () =>
    new Date(Date.now() + LESSON_RESERVATION_MIN_HOURS_AHEAD * 60 * 60 * 1000);

  const [step, setStep] = useState<number>(1);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [selectedSubject, setSelectedSubject] = useState<ISubject | null>(null);
  const [selectedDuration, setSelectedDuration] = useState<number | null>(null);
  const [selectedPrice, setSelectedPrice] = useState<number | null>(null);
  const [teachers, setTeachers] = useState<
    {
      name: string;
      firstName: string;
      image?: string;
      id: string;
    }[]
  >([]);
  const [selectedTeacher, setSelectedTeacher] = useState<string | null>(null);
  const [selectedTeacherName, setSelectedTeacherName] = useState<string | null>(
    null,
  );
  const [availableTimeslots, setAvailableTimeslots] = useState<
    {
      startTime: Date;
      endTime: Date;
      teacherName: string;
      teacherId: string;
      calendarId: number;
    }[]
  >([]);
  const [selectedTerm, setSelectedTerm] = useState<StartTimeEventEmit | null>(
    null,
  );
  const [paymentInfo, setPaymentInfo] = useState<{
    termDate: Date;
    price: number;
    teacherName: string | null;
    subjectName: string | null;
    duration: number | null;
  } | null>(null);
  const [reservation, setReservation] = useState<ReservationData | null>(null);
  const [notes, setNotes] = useState<string>("");

  usePageTitle("Rezervacija poduka");

  const fetchTimeslots = useCallback(async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const startOfDay = today.toISOString();

      const response = await api.get(`/calendars/?start_after=${startOfDay}`);
      const availableEvents = response.data.filter(
        (event: any) => event.status === "AVAILABLE",
      );

      const timeslots = availableEvents.map((event: any) => ({
        startTime: new Date(event.start),
        endTime: new Date(event.end),
        teacherName: event.teacher
          ? event.teacher.first_name + " " + event.teacher.last_name
          : "Unknown",
        teacherId: event.teacher ? event.teacher.user_id : "",
        calendarId: event.id,
      }));
      setAvailableTimeslots(timeslots);
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    const savedReservationJson = localStorage.getItem(RESERVATION_STORAGE_KEY);
    if (savedReservationJson) {
      try {
        const savedReservation = JSON.parse(savedReservationJson);

        savedReservation.reservedUntil = new Date(
          savedReservation.reservedUntil,
        );

        if (savedReservation.reservedUntil > new Date()) {
          const minimumAllowedStartTime = getMinimumAllowedLessonStartTime();
          if (
            savedReservation.selectedTerm?.startTime &&
            new Date(savedReservation.selectedTerm.startTime).getTime() <
              minimumAllowedStartTime.getTime()
          ) {
            localStorage.removeItem(RESERVATION_STORAGE_KEY);
            toast.info(
              `Termini se mogu kupiti samo ${LESSON_RESERVATION_MIN_HOURS_AHEAD}h unaprijed.`,
            );
            return;
          }

          setReservation(savedReservation);

          if (savedReservation.selectedSubject)
            setSelectedSubject(savedReservation.selectedSubject);
          if (savedReservation.selectedDuration)
            setSelectedDuration(savedReservation.selectedDuration);
          if (savedReservation.selectedPrice)
            setSelectedPrice(savedReservation.selectedPrice);
          if (savedReservation.selectedTeacher) {
            setSelectedTeacher(savedReservation.selectedTeacher);
            setSelectedTeacherName(savedReservation.selectedTeacherName);
          }
          if (savedReservation.selectedTerm)
            setSelectedTerm(savedReservation.selectedTerm);

          if (
            savedReservation.selectedSubject &&
            savedReservation.selectedPrice &&
            savedReservation.selectedTeacher &&
            savedReservation.selectedTerm
          ) {
            setPaymentInfo({
              termDate: new Date(savedReservation.selectedTerm.startTime),
              price: savedReservation.selectedPrice,
              teacherName: savedReservation.selectedTeacherName,
              subjectName: savedReservation.selectedSubject.name,
              duration: savedReservation.selectedDuration,
            });

            setStep(5);

            toast.info("Učitana je vaša rezervacija u tijeku");
          }
        } else {
          localStorage.removeItem(RESERVATION_STORAGE_KEY);
        }
      } catch (error) {
        console.error("Error parsing saved reservation:", error);
        localStorage.removeItem(RESERVATION_STORAGE_KEY);
      }
    }
  }, []);

  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const response = await api.get("/subjects/");
        const lessonSubjects: ISubject[] = response.data.filter(
          (subject: ISubject) => subject.kind === SubjectKind.LESSON,
        );
        const subjectsData: Subject[] = lessonSubjects.map(
          (subject: ISubject) => ({
            name: subject.name,
            img: subject.picture
              ? subject.picture
              : `https://awesome-lumen-web-2-31.s3.amazonaws.com/subjects/Lumenko_kauc.png`,
          }),
        );
        setSubjects(subjectsData);
      } catch (error) {
        console.error(error);
      }
    };
    fetchSubjects();
  }, []);

  useEffect(() => {
    fetchTimeslots();
  }, [fetchTimeslots]);

  useEffect(() => {
    const fetchTeachers = async () => {
      if (!selectedSubject) return;

      try {
        const response = await api.get("/teachers/");
        const filteredTeachers = response.data.filter((teacher: any) => {
          // Filter teachers who have lesson_profile
          if (!teacher.lesson_profile) return false;

          // Check if the teacher teaches the selected subject
          return (
            teacher.lesson_profile.subjects &&
            teacher.lesson_profile.subjects.some(
              (subj: any) => subj.id === selectedSubject.id,
            )
          );
        });

        const teachersList = filteredTeachers.map((teacher: any) => ({
          name: teacher.first_name + " " + teacher.last_name,
          firstName: teacher.first_name,
          image: teacher.lesson_profile?.picture || null,
          id: teacher.user_id,
        }));
        setTeachers(teachersList);
      } catch (error) {
        console.error(error);
      }
    };

    fetchTeachers();
  }, [selectedSubject]);

  const handleSubjectSelect = async (subjectName: string) => {
    try {
      const response = await api.get("/subjects/");
      const subject = response.data.find(
        (subject: ISubject) =>
          subject.name === subjectName && subject.kind === SubjectKind.LESSON,
      );

      if (subject) {
        setSelectedSubject(subject);
        setStep(2);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleDurationSelect = (minutes: number, price: number) => {
    setSelectedDuration(minutes);
    setSelectedPrice(price);
    setStep(3);
  };

  const handleTeacherSelect = (teacherId: string) => {
    setSelectedTeacher((prev) => (prev === teacherId ? null : teacherId));
    const teacher = teachers.find((inst) => inst.id === teacherId);
    setSelectedTeacherName(teacher ? teacher.firstName : null);
    setSelectedTerm(null);
    setReservation(null);

    localStorage.removeItem(RESERVATION_STORAGE_KEY);
  };

  const handleTermSelect = async (term: StartTimeEventEmit) => {
    const minimumAllowedStartTime = getMinimumAllowedLessonStartTime();
    if (term.startTime.getTime() < minimumAllowedStartTime.getTime()) {
      toast.error(
        `Termini se mogu rezervirati samo ${LESSON_RESERVATION_MIN_HOURS_AHEAD}h unaprijed.`,
      );
      return;
    }

    // Find the closest matching timeslot
    const selectedTimeslot = availableTimeslots.find((slot) => {
      // Check if the selected time falls within this slot's time range
      const slotStartTime = slot.startTime.getTime();
      const slotEndTime = slot.endTime.getTime();
      const termTime = term.startTime.getTime();

      return (
        termTime >= slotStartTime &&
        termTime + (selectedDuration || 60) * 60 * 1000 <= slotEndTime &&
        (!selectedTeacher || slot.teacherId === selectedTeacher)
      );
    });

    if (!selectedTimeslot) {
      toast.error("Odabrani termin nije dostupan");
      return;
    }

    try {
      const endTime = new Date(
        term.startTime.getTime() + (selectedDuration || 60) * 60 * 1000,
      );

      const response = await api.post(
        `/calendars/${selectedTimeslot.calendarId}/reserve/`,
        {
          start: term.startTime.toISOString(),
          end: endTime.toISOString(),
        },
      );

      if (response.data && response.data.reservation_token) {
        const reservationData = {
          reservationToken: response.data.reservation_token,
          reservedUntil: new Date(response.data.reserved_until),
          calendarId: selectedTimeslot.calendarId,
        };

        setReservation(reservationData);
        setSelectedTerm(term);

        localStorage.setItem(
          RESERVATION_STORAGE_KEY,
          JSON.stringify({
            ...reservationData,
            selectedSubject,
            selectedDuration,
            selectedPrice,
            selectedTeacher: selectedTeacher,
            selectedTeacherName: selectedTeacherName,
            selectedTerm: term,
            createdAt: new Date().toISOString(),
          }),
        );

        setStep(4);

        await fetchTimeslots();
      } else {
        toast.error("Nije moguće rezervirati termin");
      }
    } catch (error) {
      console.error("Error reserving timeslot:", error);
      toast.error("Greška prilikom rezervacije termina");
    }
  };

  const handlePaymentInfo = () => {
    if (
      selectedTerm &&
      selectedTeacherName &&
      selectedPrice &&
      selectedSubject
    ) {
      const minimumAllowedStartTime = getMinimumAllowedLessonStartTime();
      if (
        selectedTerm.startTime.getTime() < minimumAllowedStartTime.getTime()
      ) {
        toast.error(
          `Termini se mogu kupiti samo ${LESSON_RESERVATION_MIN_HOURS_AHEAD}h unaprijed.`,
        );
        return;
      }

      setPaymentInfo({
        termDate: selectedTerm.startTime,
        price: selectedPrice,
        teacherName: selectedTeacherName,
        subjectName: selectedSubject.name,
        duration: selectedDuration,
      });
      setStep(5);
    }
  };

  const handleBack = async () => {
    setStep((prev) => prev - 1);

    if (step === 4) {
      await cancelReservation();
    }
  };

  async function cancelReservationRequest() {
    if (reservation) {
      try {
        await api.post(`/calendars/${reservation.calendarId}/cancel/`, {
          reservation_token: reservation.reservationToken,
        });
      } catch (error) {
        console.error("Error canceling reservation:", error);
        toast.error("Greška prilikom poništavanja rezervacije");
      }
    }
  }

  const cancelReservation = async () => {
    if (reservation) {
      await cancelReservationRequest();

      setReservation(null);
      setSelectedTerm(null);
      localStorage.removeItem(RESERVATION_STORAGE_KEY);

      await fetchTimeslots();

      setStep(1);

      toast.info("Rezervacija je poništena");
    }
  };

  const handleReservationExpired = async () => {
    toast.error("Rezervacija je istekla, molimo započnite ponovno");

    setReservation(null);
    setSelectedTerm(null);
    localStorage.removeItem(RESERVATION_STORAGE_KEY);

    await fetchTimeslots();

    setStep(1);
  };

  const handleBookingComplete = (id: number) => {
    setReservation(null);
    localStorage.removeItem(RESERVATION_STORAGE_KEY);
  };

  const handleBookingError = () => {
    toast.error("Greška prilikom rezervacije termina. Pokušajte ponovno.");
    setStep(1);
    setReservation(null);
    localStorage.removeItem(RESERVATION_STORAGE_KEY);
  };

  return (
    <Container>
      {reservation && (
        <ReservationTimer
          reservedUntil={reservation.reservedUntil}
          onExpired={handleReservationExpired}
          onCancel={cancelReservation}
        />
      )}

      {step === 1 && (
        <SubjectsSelection
          subjects={subjects}
          onSubjectSelect={handleSubjectSelect}
        />
      )}

      {step === 2 && selectedSubject && (
        <LessonDurationSelection
          subject={selectedSubject}
          onDurationSelect={handleDurationSelect}
          onBack={handleBack}
        />
      )}

      {step === 3 && (
        <TeacherTimeSelection
          teachers={teachers}
          selectedTeacher={selectedTeacher}
          availableTimeslots={availableTimeslots}
          onTeacherSelect={handleTeacherSelect}
          onTermSelect={handleTermSelect}
          onBack={handleBack}
          selectedDuration={selectedDuration}
          selectedSubject={selectedSubject}
        />
      )}

      {step === 4 && (
        <LoginRegisterFormLessons
          onSubmit={handlePaymentInfo}
          onBack={handleBack}
        />
      )}

      {step === 5 && (
        <OrderPaymentsLesson
          reservation={reservation}
          selectedSubject={selectedSubject}
          notes={notes}
          paymentInfo={paymentInfo}
          onBookingComplete={handleBookingComplete}
          onBookingError={handleBookingError}
        />
      )}
    </Container>
  );
}

export default LessonsReservationPage;
